import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import { useTranslation } from 'react-i18next';
import styles from './ForgotPassword.module.scss';
import { IinContext } from '../../context/IinContext';
import axios from 'axios';
import CryptoJS from 'crypto-js';
import Cookies from 'js-cookie';
import { CODE_LENGTH, getCodeErrorMessage, isCodeComplete, sanitizeCode } from '../../utils/verificationCode';

export const ForgotPassword = () => {
  const { t } = useTranslation();
  const { iin, setIin, iinOrBin } = useContext(IinContext);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [showIinInput, setShowIinInput] = useState(false);
  const [showMethodSelection, setShowMethodSelection] = useState(false);
  const [showNewPasswordInput, setShowNewPasswordInput] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState('');
  const [isCodeInvalid, setIsCodeInvalid] = useState(false);
  const [userExists, setUserExists] = useState(null);
  const [timer, setTimer] = useState(0);
  const [showRequestCodeButton, setShowRequestCodeButton] = useState(true);
  const [email, setEmail] = useState('');
  const [physPerson, setPhysPerson] = useState(false);
  const [isCodeFormVisible, setIsCodeFormVisible] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');
  const [isCheckingBMG, setIsCheckingBMG] = useState(false);
  const navigate = useNavigate();
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [firstRequestSent, setFirstRequestSent] = useState(false);

  const [isCompleteResponse, setIsCompleteResponse] = useState();
  const [isExists, setIsExists] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [lastDigits, setLastDigits] = useState('');

  useEffect(() => {
    if (timer > 0) {
      const timerId = setTimeout(() => setTimer(timer - 1), 1000);
      return () => clearTimeout(timerId);
    } else if (timer === 0 && firstRequestSent) {
      setShowRequestCodeButton(true);
    }
  }, [timer, firstRequestSent]);

  useEffect(() => {
    const storedPhysPerson = sessionStorage.getItem('physPerson');
    setPhysPerson(storedPhysPerson === 'true');
  }, []);

  const getUserInfo = async (iin, token) => {
    try {
      const response = await axios.get(`/kcssobackext/sso/getInfo?iinOrBin=${iin}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (response.data.physPerson) {
        setPhysPerson(response.data.physPerson)
      }
      if (response.data) {
        sessionStorage.setItem('email', response.data.email);
        setEmail(response.data.email);
        return response.data; 
      }
      return null;
    } catch (error) {
      console.error(t('forgotPassword.getUserInfoError'), error);
      setErrorMessage(t('forgotPassword.getUserInfoError'));
      return null;
    }
  };

  const fetchRedirectUrl = async (clientId) => {
    try {
      const response = await axios.get(`/kcssobackext/client/${clientId}`);
      if (response.data && response.data.redirectUrl) {
        return response.data.redirectUrl;
      } else {
        throw new Error("Redirect URL not found in response");
      }
    } catch (error) {
      console.error("Error fetching redirect URL:", error);
      throw error;
    }
  };

  const sendPostMessage = (secretCode, backurl) => {
    if (window.opener) {
      window.opener.postMessage(
        { 
          type: 'AUTH_SECRET_CODE', 
          secretCode: secretCode,
          backurl: backurl
        },
        "*"
      );
      window.close();
    }
  };


  const redirectToReferrer = async (secretCode) => {
    const clientId = sessionStorage.getItem('client-id');
    const backurl = sessionStorage.getItem('backurl');
    
    if (!clientId) {
      console.error("Client ID is missing");
      return;
    }
  
    try {
      const validationResponse = await axios.post('/kcssobackext/sso/external/validateSecretCode', {
        secretCode: secretCode
      });
      
      if (validationResponse.data !== true) {
        Cookies.remove('secretCode');
        return;
      }

      if (window.opener) {
        sendPostMessage(secretCode, backurl);
        return;
      }

      const redirectUrl = await fetchRedirectUrl(clientId);
  
      const url = new URL(redirectUrl);
      url.searchParams.set('client-id', clientId);
      url.searchParams.set('secret-code', secretCode);
    } catch (error) {
      console.error("Error during redirection:", error);
      navigate('/');
    }
  };

  const handlePasswordSubmit = async () => {
    setIsLoading(true);
    setErrorMessage('');

    const key = CryptoJS.enc.Hex.parse('10c3417e0b38987f3b44528d999a090efd708bcef15d6357c252bcd42b4cf03d');
    const iv = CryptoJS.enc.Hex.parse('315f48391c3d3cc857128287f8457d47');
    const encryptedUsername = CryptoJS.AES.encrypt(iinOrBin, key, { iv: iv }).toString();
    const encryptedPassword = CryptoJS.AES.encrypt(password, key, { iv: iv }).toString();

    const clientId = sessionStorage.getItem('client-id');

    try {
      const response = await axios.post('/kcssobackext/sso/external/login', {
        username: encryptedUsername,
        password: encryptedPassword,
        clientId: Number(clientId)
      });

      const { access_token, refresh_token } = response.data;
      sessionStorage.setItem('access_token', access_token);
      sessionStorage.setItem('refresh_token', refresh_token);

      await getUserInfo(iinOrBin, access_token);

      redirectToReferrer(response.data);

    } catch (error) {
      if (error.response) {
        const errorText = await error.response.data;
        if (errorText.includes('401 Unauthorized')) {
          setErrorMessage(t('forgotPassword.invalidPassword'));
        } else {
          setErrorMessage(t('forgotPassword.serverError'));
        }
      } else {
        setErrorMessage(t('forgotPassword.serverError'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const sendResetCode = async () => {
    try {
      const response = await axios.post('/kcssobackext/sso/external/sendResetCode', {
        iinOrBin,
        method: "email"
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.status !== 200) {
        throw new Error(t('forgotPassword.sendCodeError'));
      }
    } catch (error) {
      console.error(t('forgotPassword.sendCodeError'), error);
      setErrorMessage(t('forgotPassword.sendCodeError'));
    }
  };

  const generateCode = async () => {
    try {
      const response = await axios.get(`/kcssobackext/sms/generate-code/${iinOrBin}`);
      if (response) {
        setShowCodeInput(true);
        setShowIinInput(false);
        setFirstRequestSent(true);
        setIsCompleteResponse(true);
        setTimer(60);
      } else {
        throw new Error(t('forgotPassword.sendCodeError'));
      }
    } catch (error) {
      console.error(t('forgotPassword.sendCodeError'), error);
      setErrorMessage(t('forgotPassword.sendCodeError'));
    }
  };

  const checkResetCode = async (iin, code) => {
    try {
      const response = await fetch('/kcssobackext/sso/external/checkResetCode', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ iinOrBin: iin, code: code })
      });

      if (!response.ok) {
        const codeErrorMessage = getCodeErrorMessage(response.status, await response.text(), t);
        if (codeErrorMessage) {
          setErrorMessage(codeErrorMessage);
          return false;
        }
        throw new Error(t('forgotPassword.checkCodeError'));
      }

      return await response.json();
    } catch (error) {
      console.error(t('forgotPassword.checkCodeError'), error);
      setErrorMessage(t('forgotPassword.checkCodeError'));
      setIsLoading(false);
      return null;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setErrorMessage('');
    setIsCodeInvalid(false);
  
    const token = sessionStorage.getItem('access_token');
    const userInfo = await getUserInfo(iinOrBin, token);
  
    if (userInfo && userInfo.exists) {
      setUserExists(true);
      setShowMethodSelection(true);
    } else {
      setUserExists(false);
      setErrorMessage(t('forgotPassword.userNotRegistered'));
    }

    setIsLoading(false);
  };

  const handleMethodEmail = async (method) => {
    setSelectedMethod(method);
    setIsLoading(true);
    setErrorMessage('');
    
    await sendResetCode(method);

    setShowCodeInput(true);
    setShowMethodSelection(false);
    setIsLoading(false);
  };

  const handleMethodSms = async (method) => {
    setSelectedMethod(method);
    setIsLoading(true);
    setErrorMessage('');
    setShowIinInput(false);
    
    await generateCode(method);

    // setShowIinInput(true);
    checkBMGStatus();
    setShowMethodSelection(false);
    setIsLoading(false);
    setTimer(60);
  };

  const checkBMGStatus = async () => {
    const intervalId = setInterval(async () => {
      try {
        if (timer === 1) {
          clearInterval(intervalId);
          setIsCheckingBMG(false);
          return;
        }
        
        const checkResponse = await axios.get(`/kcssobackext/sms/isExistsBMG/${iinOrBin}`);
        const { isCompleteResponse, isExists, isEnabled, lastDigits } = checkResponse.data;

        setIsCompleteResponse(isCompleteResponse);
        setIsExists(isExists);
        setIsEnabled(isEnabled);

        if (lastDigits) {
          setLastDigits(lastDigits);
        }

        if (isCompleteResponse) {
          clearInterval(intervalId);
          setShowIinInput(false);
          setIsCompleteResponse(true);

          if (isExists && isEnabled) {
            setIsCodeFormVisible(true);
            setSuccessMessage(t('sms.codeSentSuccess'));
            setErrorMessage('');
          } else if (isExists && !isEnabled) {
            setErrorMessage(t('sms.sendingNotPossible'));
          } else if (!isExists && !isEnabled) {
            setErrorMessage(t('sms.iinNotRegisteredInBMG'));
          } else {
            setErrorMessage(t('sms.phoneNotRegistered'));
          }

          setIsCheckingBMG(false);
          setIsLoading(false);
        }
      } catch (err) {
        clearInterval(intervalId);
        setErrorMessage(t('error.serverError'));
        console.error('Error occurred during BMG check:', err);
        setIsCheckingBMG(false);
        setIsLoading(false);
      }
    }, 5000);
  };

  const handleCodeSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setErrorMessage('');
    setIsCodeInvalid(false);

    if (!isCodeComplete(code)) {
      setIsCodeInvalid(true);
      setErrorMessage(t('error.invalidCodeFormat'));
      setIsLoading(false);
      return;
    }

    const isValid = await checkResetCode(iinOrBin, code);

    if (isValid) {
      setShowNewPasswordInput(true);
      setShowCodeInput(false);
    } else if (isValid === false) {
      setIsCodeInvalid(true);
      setErrorMessage((prev) => prev || t('forgotPassword.invalidCode'));
    } else {
      setIsCodeInvalid(true);
    }

    setIsLoading(false);
  };

  const handleNewPasswordSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setErrorMessage('');

    const key = CryptoJS.enc.Hex.parse('10c3417e0b38987f3b44528d999a090efd708bcef15d6357c252bcd42b4cf03d');
    const iv = CryptoJS.enc.Hex.parse('315f48391c3d3cc857128287f8457d47');
    const encryptedIin = CryptoJS.AES.encrypt(iinOrBin, key, { iv: iv }).toString();
    const encryptedPassword = CryptoJS.AES.encrypt(newPassword, key, { iv: iv }).toString();

    try {
      const response = await axios.put('/kcssobackext/sso/external/changeKeycloakPassword', {
        iinOrBin: encryptedIin,
        password: encryptedPassword
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.status !== 200) {
        throw new Error(t('forgotPassword.setPasswordError'));
      }

      setShowPasswordForm(true);
    } catch (error) {
      console.error(t('forgotPassword.setPasswordError'), error);
      setErrorMessage(t('forgotPassword.setPasswordError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (event) => {
    const value = event.target.value;
    setIin(value);
    setError('');
    setSuccessMessage('');
    setIsButtonDisabled(true);

    if (value.length === 12) {
      const fifthDigit = parseInt(value[4], 10);

      if (!isNaN(fifthDigit)) {
        if (fifthDigit >= 4) {
          setError(t('sms.physPersonOnlyError'));
        } else {
          setIsButtonDisabled(false);
        }
      }
    }
  };

  const handleResendCode = async () => {
    setTimer(60);
    try {
      const resendResponse = await axios.post(`/kcssobackext/sms/codeNotDelivered?iin=${iinOrBin}`);
      
      if (resendResponse.data === false) {
        setErrorMessage(t('error.invalidCode'));
      } else if (resendResponse.data === true) {
        setErrorMessage('');
        setSuccessMessage(t('sms.codeResentSuccess'));
        setTimer(60);
        setIsButtonDisabled(true);
      }
    } catch (err) {
      if (err.response && (err.response.status === 400 || err.response.status === 500)) {
        setError(t('sms.resendCodeError'));
      } else {
        setError(t('error.serverErrorResend'));
      }
      console.error('Error occurred during code resend:', err);
    }
  };

  const requestNewCode = async () => {
    setIsLoading(true);
    setErrorMessage('');
    try {
      const response = await axios.post('/kcssobackext/sso/external/sendResetCode', {
        iinOrBin: iinOrBin,
        method: 'email'
      }, {
        headers: {
          'Content-Type': 'application/json',
        }
      });
  
      if (response.status !== 200) {
        throw new Error(t('forgotPassword.newCodeRequestError'));
      }
  
      setTimer(60);
      setShowRequestCodeButton(false);
  
    } catch (error) {
      console.error(t('forgotPassword.newCodeRequestError'), error);
      setErrorMessage(t('forgotPassword.newCodeRequestError'));
    } finally {
      setIsLoading(false);
    }
  };

  const switchMethod = () => {
    const newMethod = selectedMethod === 'sms' ? 'email' : 'sms';
    setSelectedMethod(newMethod);
  
    if (newMethod === 'sms' && !lastDigits) {
      checkBMGStatus();
    }
  };
  
  useEffect(() => {
    if (selectedMethod === 'sms' && !lastDigits) {
      checkBMGStatus(); 
    }
  }, [selectedMethod]);

  const handleIinKeyDown = (e) => {
    if (e.key === 'Enter' && iin.length === 12 && !isButtonDisabled) {
      handleSubmit(e);
    }
  };

  const handleCodeKeyDown = (e) => {
    if (e.key === 'Enter' && code) {
      handleCodeSubmit(e);
    }
  };

  const handleNewPasswordKeyDown = (e) => {
    if (e.key === 'Enter' && newPassword) {
      handleNewPasswordSubmit(e);
    }
  };

  const handlePasswordKeyDown = (e) => {
    if (e.key === 'Enter' && password) {
      handlePasswordSubmit();
    }
  };

  return (
    <div className={styles.container}>
      <Paper className={styles.paper}>
        {!showPasswordForm && <h2 className={styles.title}>{t('forgotPassword.title')}</h2>}
        {userExists === false ? (
          <p>{t('forgotPassword.userNotRegistered')}</p>
        ) : (
          <>
            {showPasswordForm ? (
              <>
                <h2 className={styles.title}>{t('forgotPassword.enterPassword')}</h2>
                {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}
                {userExists === true && (
                  <div className={styles.passwordContainer}>
                    <TextField
                      type="password"
                      label={t('forgotPassword.passwordLabel')}
                      variant="outlined"
                      margin="normal"
                      className={styles.loginInput}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      onKeyDown={handlePasswordKeyDown}
                    />
                    <Button
                      className={styles.loginButton}
                      size="large"
                      variant="contained"
                      onClick={handlePasswordSubmit}
                      disabled={isLoading}
                    >
                      {isLoading ? <CircularProgress size={24} /> : t('forgotPassword.login')}
                    </Button>
                  </div>
                )}
              </>
            ) : showNewPasswordInput ? (
              <form onSubmit={handleNewPasswordSubmit} className={styles.form}>
                <TextField
                  type="password"
                  label={t('forgotPassword.newPasswordLabel')}
                  variant="outlined"
                  margin="normal"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  onKeyDown={handleNewPasswordKeyDown}
                  className={styles.input}
                  required
                />
                <Button
                  type="submit"
                  variant="contained"
                  className={styles.button}
                >
                  OK
                </Button>
              </form>
            ) : showMethodSelection ? (
              <div className={styles.methodSelection}>
                <p>{t('forgotPassword.methodSelection')}</p>
                <div className={styles.buttonGroup}>
                  <Button 
                    variant="contained" 
                    disabled={!physPerson} 
                    className={styles.button} 
                    onClick={() => handleMethodSms('sms')}
                  >
                    SMS
                  </Button>
                  <Button variant="contained" className={styles.button} onClick={() => handleMethodEmail('email')}>{t('forgotPassword.email')}</Button>
                </div>
              </div>
            ) : showIinInput ? (
              <form onSubmit={handleCodeSubmit} className={styles.form}>
                <h2>{t('sms.enterIin')}</h2>
                <TextField
                  label={t('register.iin')}
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={iin}
                  onChange={handleInputChange}
                  onKeyDown={handleIinKeyDown}
                  error={!!error}
                  helperText={error}
                  disabled={true}
                />
                <>
                  {!firstRequestSent && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={generateCode}
                      style={{ width: "300px", marginTop: "50px" }}
                      disabled={isButtonDisabled || isCheckingBMG}
                    >
                      {t('sms.switchToSms')}
                    </Button>
                  )}
                  {firstRequestSent && timer > 0 && (
                    <p>{t('sms.resendCodeIn')} {timer} сек.</p>
                  )}
                  {firstRequestSent && timer === 0 && (
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={handleResendCode}
                      style={{ width: "300px", marginTop: "20px" }}
                      disabled={isButtonDisabled}
                    >
                      {t('sms.resendCode')}
                    </Button>
                  )}
                </>
              </form>
            ) : showCodeInput ? (
              <form onSubmit={handleCodeSubmit} className={styles.form}>
                {selectedMethod === 'email' && email && (
                  <p>{t('forgotPassword.emailHint')} {email}</p>
                )}

                {selectedMethod === 'sms' && lastDigits && (
                  <p>{t('forgotPassword.numberHint')} {lastDigits}</p>
                )}
                
                <TextField
                  type="text"
                  label={t('forgotPassword.enterCode', { method: selectedMethod === 'sms' ? 'SMS' : 'email' })}
                  variant="outlined"
                  margin="normal"
                  value={code}
                  onChange={e => setCode(sanitizeCode(e.target.value))}
                  onKeyDown={handleCodeKeyDown}
                  className={styles.input}
                  required
                  inputProps={{ maxLength: CODE_LENGTH, inputMode: 'numeric', pattern: '[0-9]*' }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  className={styles.button}
                >
                  {t('forgotPassword.submit')}
                </Button>
                {isCodeInvalid && (
                  <p className={styles.error}>{errorMessage}</p>
                )}
                <Button
                  variant="text"
                  className={styles.switchMethod}
                  onClick={switchMethod}
                >
                  {selectedMethod === 'sms' ? t('forgotPassword.switchToEmail') : t('forgotPassword.switchToSms')}
                </Button>
                {showRequestCodeButton ? (
                  <Button
                    variant="text"
                    className={styles.switchMethod}
                    onClick={requestNewCode}
                  >
                    {selectedMethod === 'sms' ? t('forgotPassword.requestNewSmsCode') : t('forgotPassword.requestNewEmailCode')}
                  </Button>
                ) : (
                  <p>{t('forgotPassword.wait')} { timer }</p>
                )}
              </form>
            ) : isCompleteResponse === true ? (
              <form onSubmit={handleCodeSubmit} className={styles.form}>
                <TextField
                  type="text"
                  label={t('forgotPassword.enterCode', { method: selectedMethod === 'sms' ? 'SMS' : 'email' })}
                  variant="outlined"
                  margin="normal"
                  value={code}
                  onChange={e => setCode(sanitizeCode(e.target.value))}
                  onKeyDown={handleCodeKeyDown}
                  className={styles.input}
                  required
                  inputProps={{ maxLength: CODE_LENGTH, inputMode: 'numeric', pattern: '[0-9]*' }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  className={styles.button}
                >
                  {t('forgotPassword.submit')}
                </Button>
                {isCodeInvalid && (
                  <p className={styles.error}>{errorMessage}</p>
                )}
                <Button
                  variant="text"
                  className={styles.switchMethod}
                  onClick={switchMethod}
                >
                  {selectedMethod === 'sms' ? t('forgotPassword.switchToEmail') : t('forgotPassword.switchToSms')}
                </Button>
                {showRequestCodeButton ? (
                  <Button
                    variant="text"
                    className={styles.switchMethod}
                    onClick={requestNewCode}
                  >
                    {selectedMethod === 'sms' ? t('forgotPassword.requestNewSmsCode') : t('forgotPassword.requestNewEmailCode')}
                  </Button>
                ) : (
                  <p>{t('forgotPassword.wait')} { timer }</p>
                )}
              </form>
            ) : (
              <form onSubmit={handleSubmit} className={styles.form}>
                <TextField
                  type="text"
                  label={t('forgotPassword.enterIinBin')}
                  variant="outlined"
                  margin="normal"
                  value={iin ? iin : iinOrBin}
                  onChange={e => setIin(e.target.value.replace(/\D/, ''))}
                  onKeyDown={handleIinKeyDown}
                  className={styles.input}
                  required
                  disabled={true}
                  inputProps={{ maxLength: 12 }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  className={styles.button}
                  disabled={isLoading}
                >
                  OK
                </Button>
                {isLoading && <CircularProgress className={styles.loader} />}
                {errorMessage && <p className={styles.error}>{errorMessage}</p>}
              </form>
            )}
          </>
        )}
      </Paper>
    </div>
  );
};
